import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, LockKeyhole } from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import ClubAbout from './ClubAbout';

export default function AccessGate({ children }) {
  const [isVerified, setIsVerified] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef(null);
  const pending = useRef(false);
  useEffect(() => {
    const loader = document.getElementById('initial-loader');
    if (loader) loader.style.display = 'none';
    let cancelled = false;
    const checkToken = async () => {
      const token = localStorage.getItem('tapAccessToken');
      if (!token || !isSupabaseConfigured) return;
      try {
        const { data, error: rpcError } = await supabase.rpc('verify_access_token', { input_token: token });
        if (cancelled) return;
        if (rpcError) { setError('회원 인증을 확인하지 못했습니다. 연결 상태를 확인한 뒤 다시 시도해 주세요.'); return; }
        if (data === true) setIsVerified(true);
        else localStorage.removeItem('tapAccessToken');
      } catch {
        if (!cancelled) setError('연결이 원활하지 않습니다. 잠시 후 다시 시도해 주세요.');
      }
    };
    checkToken().finally(() => { if (!cancelled) setIsChecking(false); });
    return () => { cancelled = true; };
  }, []);
  const handleSubmit = async e => {
    e.preventDefault();
    if (!code.trim() || pending.current) return;
    if (!isSupabaseConfigured) { setError('회원 서비스 연결을 준비 중입니다. 운영진에게 문의해 주세요.'); return; }
    pending.current = true; setIsSubmitting(true); setError('');
    try {
      const { data: token, error: rpcError } = await supabase.rpc('verify_access_code', { input_code: code.trim().normalize('NFC') });
      if (rpcError) {
        setError(rpcError.message?.includes('TOO_MANY_ATTEMPTS') ? '입장 시도가 많습니다. 잠시 후 다시 시도해 주세요.' : '입장을 확인하지 못했습니다. 회원 코드를 확인하거나 잠시 후 다시 시도해 주세요.');
        return;
      }
      // A successful HTTP response with a null/false token is still a denied login.
      if (typeof token !== 'string' || !token.trim()) { setError('회원 코드가 일치하지 않습니다. 운영진이 안내한 코드를 확인해 주세요.'); return; }
      localStorage.setItem('tapAccessToken', token);
      setCode(''); setIsVerified(true);
    } catch { setError('서버에 연결할 수 없습니다. 입력한 코드는 유지됩니다. 다시 시도해 주세요.'); }
    finally { pending.current = false; setIsSubmitting(false); }
  };
  if (isVerified) return children;
  return <div className="club-entry-page">
    <header className="club-header"><div className="club-header-inner"><span className="club-wordmark">TAP<span>.</span><small>TENNIS CLUB</small></span><span className="club-eyebrow">JEJU NATIONAL UNIVERSITY</span></div></header>
    <main className="club-entry">
      <ClubAbout onEnter={() => { inputRef.current?.scrollIntoView({ block: 'center', behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }); inputRef.current?.focus({ preventScroll: true }); }} />
      <section className="club-panel club-entry-form" aria-labelledby="member-entry-title">
        <LockKeyhole size={24} className="text-primary mb-4" aria-hidden="true" /><h2 id="member-entry-title">회원 입장</h2><p className="text-sm text-muted-foreground mt-2">TAP 회원의 일정과 경기 기록을 확인하세요.</p>
        {isChecking ? <p role="status" className="club-status">회원 인증을 확인하고 있습니다…</p> : <form onSubmit={handleSubmit} aria-busy={isSubmitting}>
          <label htmlFor="member-code">회원 코드</label><input ref={inputRef} id="member-code" type="password" value={code} onChange={e => { setCode(e.target.value); setError(''); }} placeholder="운영진이 안내한 코드" maxLength={100} autoComplete="current-password" autoCapitalize="none" spellCheck={false} aria-invalid={!!error} aria-describedby={error ? 'entry-error entry-help' : 'entry-help'} required />
          {error && <p id="entry-error" role="alert" className="club-error">{error}</p>}
          <button type="submit" className="club-button" disabled={isSubmitting}>{isSubmitting ? '입장 확인 중…' : '회원 공간으로 입장'}<ArrowRight size={18} aria-hidden="true" /></button>
          <p id="entry-help" className="text-sm text-muted-foreground mt-4">인증이 유지되는 동안 다음 방문부터 바로 입장합니다.</p>
        </form>}
        <details><summary>처음 오셨거나 회원 코드가 없나요?</summary><p>동아리 운영진에게 가입 안내와 회원 코드를 요청해 주세요. 코드는 회원에게만 공유해 주세요.</p></details>
      </section>
    </main><footer className="text-center text-xs text-muted-foreground px-5 pb-8">TAP · Tennis Advantage Player Club</footer>
  </div>;
}
